import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { OrderModel } from '@/types/model/Order'
import { OrderItemRow } from './OrderItemRow'
import { AddItemDialog } from './AddItemDialog'

interface OrderItemsTableProps {
    order: OrderModel.Order
    loading: boolean
    actions: {
        updateItemQuantity: (id: string, qty: number) => void
        updateItemDiscount: (id: string, discount: number) => void
        removeItem: (id: string) => void
        updateDiscount: (discount: number) => void
        addCoupon: (code: string) => void
        removeCoupon: () => void
        addItem: (data: any) => Promise<void>
    }
    isEditable?: boolean
}

/** The order's line items; rendered inside the "Items" tab of the order detail card. */
export const OrderItemsTable = ({ order, loading, actions, isEditable = true }: OrderItemsTableProps) => {
    const [isAddItemOpen, setIsAddItemOpen] = useState(false)
    const items = order.items.length > 0 ? order.items : order.order_items

    const handleAddItem = async (data: any) => {
        await actions.addItem(data)
        setIsAddItemOpen(false)
    }

    return (
        <div>
            <div className="flex items-center justify-between gap-3 p-4">
                <h3 className="text-heading-md text-ink">Order items ({items.length})</h3>
                {isEditable && (
                    <Button variant="secondary" onClick={() => setIsAddItemOpen(true)}>
                        <Plus />
                        Add item
                    </Button>
                )}
            </div>
            <div className="border-t border-border">
                <Table className="min-w-full md:min-w-[600px]">
                    <TableHeader className="hidden md:table-header-group">
                        <TableRow className="hover:bg-transparent">
                            <TableHead className="min-w-[200px]">Product</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead>Quantity</TableHead>
                            <TableHead className="text-right">Discount</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="w-12">
                                <span className="sr-only">Actions</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item) => (
                            <OrderItemRow
                                key={item._id}
                                item={item}
                                loading={loading}
                                isEditable={isEditable}
                                onUpdateQuantity={actions.updateItemQuantity}
                                onUpdateDiscount={actions.updateItemDiscount}
                                onRemove={actions.removeItem}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <AddItemDialog
                open={isAddItemOpen}
                onOpenChange={setIsAddItemOpen}
                onAdd={handleAddItem}
                loading={loading}
            />
        </div>
    )
}
